/**
 * SingleLineDiagram - IEEE/IEC Standard Style single line diagram
 * with animated power flow indicators.
 */

import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { COLORS } from './ResultsDesignTokens';
import { vortxTypography } from '@vortx/shared-ui';

// Single Line Diagram Component - IEEE/IEC Standard Style
export const SingleLineDiagram = ({ topology, result }) => {
    const isDCCoupled = topology?.id >= 2 && topology?.id <= 4;

    // Power flow values for display
    const solarKw = result?.total_solar_kwh ? Math.round(result.total_solar_kwh / 24) : 0;
    const evKw = result?.total_ev_kwh ? Math.round(result.total_ev_kwh / 24) : 0;
    const gridKw = result?.total_grid_import_kwh ? Math.round(result.total_grid_import_kwh / 24) : 0;
    const bessKw = result?.total_bess_throughput_kwh ? Math.round(result.total_bess_throughput_kwh / 48) : 0;
    const gridExporting = (result?.total_grid_export_kwh || 0) > (result?.total_grid_import_kwh || 0);

    // Standard electrical component colors
    const busColor = '#4ecdc4';
    const lineWidth = 2;

    // Circuit Breaker Symbol (IEEE standard - two parallel lines with arc)
    const CircuitBreaker = ({ x, y, color, closed = true }) => (
        <g transform={`translate(${x}, ${y})`}>
            <line x1="0" y1="-8" x2="0" y2="-4" stroke={color} strokeWidth={lineWidth} />
            <line x1="0" y1="4" x2="0" y2="8" stroke={color} strokeWidth={lineWidth} />
            {closed ? (
                <>
                    <line x1="-4" y1="-4" x2="4" y2="-4" stroke={color} strokeWidth={lineWidth} />
                    <line x1="-4" y1="4" x2="4" y2="4" stroke={color} strokeWidth={lineWidth} />
                    <line x1="0" y1="-4" x2="0" y2="4" stroke={color} strokeWidth={lineWidth + 1} />
                </>
            ) : (
                <>
                    <line x1="-4" y1="-4" x2="4" y2="-4" stroke={color} strokeWidth={lineWidth} />
                    <line x1="-4" y1="4" x2="4" y2="4" stroke={color} strokeWidth={lineWidth} />
                    <line x1="0" y1="-4" x2="4" y2="4" stroke={color} strokeWidth={lineWidth + 1} />
                </>
            )}
        </g>
    );

    // Inverter Symbol (rectangle with DC/AC labels or ~ symbol)
    const InverterSymbol = ({ x, y, color, label = 'INV', bidir = false }) => (
        <g transform={`translate(${x}, ${y})`}>
            <rect x="-20" y="-12" width="40" height="24" rx="2" fill={COLORS.bg.card} stroke={color} strokeWidth={lineWidth} />
            {bidir ? (
                <>
                    <text x="-8" y="4" fill={color} fontSize="8" fontFamily={vortxTypography.mono} fontWeight="600">{'\u2194'}</text>
                    <text x="4" y="4" fill={color} fontSize="7" fontFamily={vortxTypography.mono}>~</text>
                </>
            ) : (
                <>
                    <text x="-10" y="4" fill={color} fontSize="7" fontFamily={vortxTypography.mono}>=</text>
                    <text x="2" y="4" fill={color} fontSize="7" fontFamily={vortxTypography.mono}>~</text>
                    <line x1="-2" y1="-8" x2="-2" y2="8" stroke={color} strokeWidth="1" strokeDasharray="2 2" />
                </>
            )}
            <text x="0" y="22" fill={color} fontSize="8" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="600">{label}</text>
        </g>
    );

    return (
        <Box sx={{ width: '100%', height: 340, position: 'relative' }}>
            <svg width="100%" height="100%" viewBox="0 0 720 320" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <filter id="sldGlow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="2" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    {/* Arrow marker for power flow */}
                    <marker id="arrowSolar" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                        <path d="M0,1 L8,4 L0,7 Z" fill={COLORS.solar} />
                    </marker>
                    <marker id="arrowGrid" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                        <path d="M0,1 L8,4 L0,7 Z" fill={COLORS.grid} />
                    </marker>
                    <marker id="arrowEv" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                        <path d="M0,1 L8,4 L0,7 Z" fill={COLORS.ev} />
                    </marker>
                    <marker id="arrowBess" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                        <path d="M0,1 L8,4 L0,7 Z" fill={COLORS.bess} />
                    </marker>
                </defs>

                {/* ========== MAIN AC BUS ========== */}
                {/* Thick horizontal bus bar - standard representation */}
                <rect x="60" y="155" width="600" height="10" fill={busColor} rx="2" filter="url(#sldGlow)" />
                <text x="360" y="150" fill={busColor} fontSize="11" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="700">
                    480V AC BUS
                </text>

                {/* ========== UTILITY GRID (Left) ========== */}
                <g>
                    {/* Grid symbol - standard utility source (circle with 3 lines) */}
                    <circle cx="90" cy="55" r="24" fill="none" stroke={COLORS.grid} strokeWidth="2.5" filter="url(#sldGlow)" />
                    <line x1="78" y1="48" x2="102" y2="48" stroke={COLORS.grid} strokeWidth="2" />
                    <line x1="78" y1="55" x2="102" y2="55" stroke={COLORS.grid} strokeWidth="2" />
                    <line x1="78" y1="62" x2="102" y2="62" stroke={COLORS.grid} strokeWidth="2" />
                    <text x="90" y="25" fill={COLORS.grid} fontSize="10" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="700">UTILITY</text>
                    <text x="90" y="95" fill={COLORS.text.muted} fontSize="9" fontFamily={vortxTypography.mono} textAnchor="middle">{gridKw} kW</text>

                    {/* Connection line to bus */}
                    <line x1="90" y1="79" x2="90" y2="110" stroke={COLORS.grid} strokeWidth={lineWidth} />

                    {/* Meter symbol */}
                    <circle cx="90" cy="118" r="8" fill={COLORS.bg.card} stroke={COLORS.grid} strokeWidth={lineWidth} />
                    <text x="90" y="121" fill={COLORS.grid} fontSize="6" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="600">M</text>
                    <text x="108" y="121" fill={COLORS.text.muted} fontSize="7" fontFamily={vortxTypography.mono}>METER</text>

                    {/* Line to breaker */}
                    <line x1="90" y1="126" x2="90" y2="135" stroke={COLORS.grid} strokeWidth={lineWidth} />

                    {/* Circuit breaker */}
                    <CircuitBreaker x={90} y={143} color={COLORS.grid} closed={true} />

                    {/* Connect to bus */}
                    <line x1="90" y1="151" x2="90" y2="155" stroke={COLORS.grid} strokeWidth={lineWidth} />

                    {/* Flow arrow */}
                    {!gridExporting && gridKw > 0 && (
                        <polygon points="85,105 90,95 95,105" fill={COLORS.grid}>
                            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" repeatCount="indefinite" />
                        </polygon>
                    )}
                    {gridExporting && (
                        <polygon points="85,95 90,105 95,95" fill={COLORS.grid}>
                            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" repeatCount="indefinite" />
                        </polygon>
                    )}
                </g>

                {/* ========== PV ARRAY ========== */}
                <g>
                    {/* PV symbol - solar array (rectangle with diagonal) */}
                    <rect x="195" y="35" width="50" height="35" rx="2" fill="none" stroke={COLORS.solar} strokeWidth="2.5" filter="url(#sldGlow)" />
                    <line x1="195" y1="35" x2="245" y2="70" stroke={COLORS.solar} strokeWidth="1.5" />
                    <line x1="207" y1="35" x2="245" y2="58" stroke={COLORS.solar} strokeWidth="1" opacity="0.6" />
                    <line x1="195" y1="47" x2="233" y2="70" stroke={COLORS.solar} strokeWidth="1" opacity="0.6" />
                    <text x="220" y="25" fill={COLORS.solar} fontSize="10" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="700">PV ARRAY</text>
                    <text x="220" y="90" fill={COLORS.text.muted} fontSize="9" fontFamily={vortxTypography.mono} textAnchor="middle">{solarKw} kW</text>

                    {/* DC line to inverter */}
                    <line x1="220" y1="70" x2="220" y2="100" stroke={COLORS.solar} strokeWidth={lineWidth} />
                    <text x="232" y="85" fill={COLORS.dc} fontSize="7" fontFamily={vortxTypography.mono}>DC</text>

                    {/* Inverter */}
                    <InverterSymbol x={220} y={116} color={COLORS.solar} label="PV INV" />

                    {/* AC line to breaker */}
                    <line x1="220" y1="128" x2="220" y2="135" stroke={COLORS.solar} strokeWidth={lineWidth} />

                    {/* Circuit breaker */}
                    <CircuitBreaker x={220} y={143} color={COLORS.solar} closed={true} />

                    {/* Connect to bus */}
                    <line x1="220" y1="151" x2="220" y2="155" stroke={COLORS.solar} strokeWidth={lineWidth} />

                    {/* Flow arrow */}
                    {solarKw > 0 && (
                        <polygon points="215,95 220,85 225,95" fill={COLORS.solar}>
                            <animate attributeName="opacity" values="0.3;1;0.3" dur="1s" repeatCount="indefinite" />
                        </polygon>
                    )}
                </g>

                {/* ========== BESS (Battery) ========== */}
                <g>
                    {/* Battery symbol - standard IEEE */}
                    <g transform="translate(350, 240)">
                        {/* Long plate */}
                        <line x1="-18" y1="0" x2="18" y2="0" stroke={COLORS.bess} strokeWidth="4" />
                        {/* Short plate */}
                        <line x1="-10" y1="8" x2="10" y2="8" stroke={COLORS.bess} strokeWidth="4" />
                        {/* Additional cell indicators */}
                        <line x1="-14" y1="16" x2="14" y2="16" stroke={COLORS.bess} strokeWidth="3" />
                        <line x1="-8" y1="24" x2="8" y2="24" stroke={COLORS.bess} strokeWidth="3" />
                    </g>
                    <rect x="320" y="225" width="60" height="55" rx="3" fill="none" stroke={COLORS.bess} strokeWidth="2" filter="url(#sldGlow)" />
                    <text x="350" y="220" fill={COLORS.bess} fontSize="10" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="700">BESS</text>
                    <text x="350" y="295" fill={COLORS.text.muted} fontSize="9" fontFamily={vortxTypography.mono} textAnchor="middle">{bessKw} kW</text>

                    {/* Line from bus down */}
                    <line x1="350" y1="165" x2="350" y2="175" stroke={COLORS.bess} strokeWidth={lineWidth} />

                    {/* Circuit breaker */}
                    <CircuitBreaker x={350} y={183} color={COLORS.bess} closed={true} />

                    {/* Line to inverter */}
                    <line x1="350" y1="191" x2="350" y2="195" stroke={COLORS.bess} strokeWidth={lineWidth} />

                    {/* Bidirectional inverter */}
                    <InverterSymbol x={350} y={207} color={COLORS.bess} label="BI-DIR" bidir={true} />

                    {/* DC line to battery */}
                    <line x1="350" y1="219" x2="350" y2="225" stroke={COLORS.bess} strokeWidth={lineWidth} />
                    <text x="362" y="222" fill={COLORS.dc} fontSize="7" fontFamily={vortxTypography.mono}>DC</text>

                    {/* Bidirectional flow arrows */}
                    <g>
                        <polygon points="345,180 350,170 355,180" fill={COLORS.bess}>
                            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite" />
                        </polygon>
                        <polygon points="345,192 350,202 355,192" fill={COLORS.bess}>
                            <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
                        </polygon>
                    </g>
                </g>

                {/* ========== EV CHARGERS ========== */}
                <g>
                    {/* EV Charger symbol - load with plug */}
                    <rect x="455" y="225" width="50" height="50" rx="3" fill="none" stroke={COLORS.ev} strokeWidth="2.5" filter="url(#sldGlow)" />
                    {/* Plug prongs */}
                    <line x1="470" y1="245" x2="470" y2="260" stroke={COLORS.ev} strokeWidth="4" strokeLinecap="round" />
                    <line x1="490" y1="245" x2="490" y2="260" stroke={COLORS.ev} strokeWidth="4" strokeLinecap="round" />
                    {/* Ground */}
                    <circle cx="480" cy="258" r="3" fill={COLORS.ev} />
                    <text x="480" y="220" fill={COLORS.ev} fontSize="10" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="700">EV LOAD</text>
                    <text x="480" y="290" fill={COLORS.text.muted} fontSize="9" fontFamily={vortxTypography.mono} textAnchor="middle">{evKw} kW</text>

                    {/* Line from bus down */}
                    <line x1="480" y1="165" x2="480" y2="175" stroke={COLORS.ev} strokeWidth={lineWidth} />

                    {/* Circuit breaker */}
                    <CircuitBreaker x={480} y={183} color={COLORS.ev} closed={true} />

                    {/* Line to charger */}
                    <line x1="480" y1="191" x2="480" y2="225" stroke={COLORS.ev} strokeWidth={lineWidth} />

                    {/* Flow arrow */}
                    {evKw > 0 && (
                        <polygon points="475,200 480,210 485,200" fill={COLORS.ev}>
                            <animate attributeName="opacity" values="0.3;1;0.3" dur="0.8s" repeatCount="indefinite" />
                        </polygon>
                    )}
                </g>

                {/* ========== ADDITIONAL LOADS (Right) ========== */}
                <g>
                    {/* Generic load symbol - zigzag/resistor */}
                    <path
                        d="M610,225 L610,235 L600,240 L620,250 L600,260 L620,270 L610,275 L610,285"
                        fill="none"
                        stroke={COLORS.text.secondary}
                        strokeWidth="2"
                    />
                    <text x="610" y="220" fill={COLORS.text.secondary} fontSize="10" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="700">AUX</text>
                    <text x="610" y="300" fill={COLORS.text.muted} fontSize="8" fontFamily={vortxTypography.mono} textAnchor="middle">LOADS</text>

                    {/* Line from bus down */}
                    <line x1="610" y1="165" x2="610" y2="175" stroke={COLORS.text.secondary} strokeWidth={lineWidth} />

                    {/* Circuit breaker */}
                    <CircuitBreaker x={610} y={183} color={COLORS.text.secondary} closed={true} />

                    {/* Line to load */}
                    <line x1="610" y1="191" x2="610" y2="225" stroke={COLORS.text.secondary} strokeWidth={lineWidth} />
                </g>

                {/* ========== LEGEND ========== */}
                <g transform="translate(580, 20)">
                    <rect x="0" y="0" width="130" height="70" rx="4" fill={alpha(COLORS.bg.card, 0.8)} stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                    <text x="10" y="16" fill={COLORS.text.muted} fontSize="8" fontFamily={vortxTypography.mono} fontWeight="600">TOPOLOGY</text>
                    <text x="10" y="32" fill={COLORS.accent} fontSize="9" fontFamily={vortxTypography.mono} fontWeight="600">
                        {isDCCoupled ? 'DC-COUPLED' : 'AC-COUPLED'}
                    </text>
                    <line x1="10" y1="40" x2="120" y2="40" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                    <text x="10" y="54" fill={COLORS.text.muted} fontSize="7" fontFamily={vortxTypography.mono}>
                        Topology #{topology?.id || '?'}
                    </text>
                    <text x="10" y="64" fill={COLORS.text.secondary} fontSize="7" fontFamily={vortxTypography.mono}>
                        {topology?.name?.slice(0, 18) || 'Unknown'}
                    </text>
                </g>

                {/* ========== CONNECTION POINT INDICATORS ========== */}
                {/* Small dots at bus connection points */}
                <circle cx="90" cy="160" r="3" fill={COLORS.grid} />
                <circle cx="220" cy="160" r="3" fill={COLORS.solar} />
                <circle cx="350" cy="160" r="3" fill={COLORS.bess} />
                <circle cx="480" cy="160" r="3" fill={COLORS.ev} />
                <circle cx="610" cy="160" r="3" fill={COLORS.text.secondary} />
            </svg>
        </Box>
    );
};
